import { useCallback, useEffect, useState } from "react";
import { Plus, KeyRound, Copy, Check, Trash2, ShieldCheck } from "lucide-react";
import { api } from "../auth/api";
import { useAuth } from "../auth/AuthContext";
import { MODULES } from "../config/modules";
import {
  PageHeader, Card, Button, Badge, Modal, Field, inputClass, Spinner, ErrorNote, Empty,
} from "../components/ui";

const DEFAULT_ON = ["home", "tools", "announcements"];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // user object or "new"
  const [secret, setSecret] = useState(null); // { username, password } shown once

  const load = useCallback(async () => {
    try {
      setUsers(await api("/admin/users"));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try {
      await api(`/admin/users/${u.id}`, { method: "PATCH", body: { active: !u.active } });
      load();
    } catch (e) { setError(e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.username}? This cannot be undone.`)) return;
    try {
      await api(`/admin/users/${u.id}`, { method: "DELETE" });
      load();
    } catch (e) { setError(e.message); }
  };

  const resetPassword = async (u) => {
    const custom = window.prompt(
      `New password for ${u.username}.\nLeave empty to auto-generate a strong one (min 8 characters if you type one).`,
      ""
    );
    if (custom === null) return;
    try {
      const { password } = await api(`/admin/users/${u.id}/reset-password`, {
        method: "POST",
        body: { password: custom || undefined, mustChangePassword: false },
      });
      setSecret({ username: u.username, password });
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        subtitle="Create accounts, choose which sections each person can open, and reset passwords at any time."
        actions={<Button onClick={() => setEditing("new")}><Plus size={16} /> Add user</Button>}
      />

      <ErrorNote>{error}</ErrorNote>

      {!users ? (
        <Spinner />
      ) : users.length === 0 ? (
        <Empty>No users yet.</Empty>
      ) : (
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-3 font-medium">User</th>
                  <th className="px-5 py-3 font-medium">Access</th>
                  <th className="px-5 py-3 font-medium">Last login</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                  <tr key={u.id} className={u.active ? "" : "bg-gray-50/70"}>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-[#111111]">{u.fullName || u.username}</p>
                      <p className="text-xs text-gray-500">@{u.username}{u.designation ? ` · ${u.designation}` : ""}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      {u.role === "admin" ? (
                        <Badge tone="brand"><ShieldCheck size={12} /> Administrator</Badge>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {u.modules.length === 0 && <span className="text-xs text-gray-400">None</span>}
                          {u.modules.map((k) => (
                            <Badge key={k}>{MODULES.find((m) => m.key === k)?.label || k}</Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-gray-600 whitespace-nowrap">
                      {u.lastLoginAt ? new Date(u.lastLoginAt.replace(" ", "T") + "Z").toLocaleString() : "Never"}
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge tone={u.active ? "good" : "neutral"}>{u.active ? "Active" : "Disabled"}</Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => setEditing(u)}>Edit access</Button>
                        <Button size="sm" variant="outline" onClick={() => resetPassword(u)}>
                          <KeyRound size={13} /> Password
                        </Button>
                        {u.id !== me.id && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => toggleActive(u)}>
                              {u.active ? "Disable" : "Enable"}
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => remove(u)} aria-label="Delete user">
                              <Trash2 size={13} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && (
        <UserForm
          existing={editing === "new" ? null : editing}
          isSelf={editing !== "new" && editing.id === me.id}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null);
            if (created) setSecret(created);
            load();
          }}
        />
      )}

      {secret && <SecretModal secret={secret} onClose={() => setSecret(null)} />}
    </div>
  );
}

function UserForm({ existing, isSelf, onClose, onSaved }) {
  const [form, setForm] = useState({
    username: existing?.username || "",
    fullName: existing?.fullName || "",
    email: existing?.email || "",
    designation: existing?.designation || "",
    role: existing?.role || "user",
    modules: existing ? existing.modules : DEFAULT_ON,
    password: "",
    mustChangePassword: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggle = (key) =>
    set("modules", form.modules.includes(key) ? form.modules.filter((k) => k !== key) : [...form.modules, key]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (existing) {
        await api(`/admin/users/${existing.id}`, {
          method: "PATCH",
          body: {
            fullName: form.fullName, email: form.email, designation: form.designation,
            role: form.role, modules: form.modules,
          },
        });
        onSaved();
      } else {
        const res = await api("/admin/users", {
          method: "POST",
          body: { ...form, password: form.password || undefined },
        });
        onSaved({ username: res.user.username, password: res.password });
      }
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  return (
    <Modal title={existing ? `Edit ${existing.username}` : "Add user"} onClose={onClose} width="max-w-xl">
      <form onSubmit={save} className="space-y-4">
        <ErrorNote>{error}</ErrorNote>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Username">
            <input className={inputClass} value={form.username} onChange={(e) => set("username", e.target.value)} disabled={!!existing} required />
          </Field>
          <Field label="Full name">
            <input className={inputClass} value={form.fullName} onChange={(e) => set("fullName", e.target.value)} required />
          </Field>
          <Field label="Email">
            <input className={inputClass} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Designation">
            <input className={inputClass} value={form.designation} onChange={(e) => set("designation", e.target.value)} />
          </Field>
        </div>

        {!existing && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Password" hint="Leave empty to auto-generate a strong one.">
              <input className={inputClass} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Auto-generate" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-gray-700 sm:mt-6">
              <input type="checkbox" checked={form.mustChangePassword} onChange={(e) => set("mustChangePassword", e.target.checked)} className="accent-[#C00000]" />
              Ask them to change it on first login
            </label>
          </div>
        )}

        <Field label="Role">
          <select
            className={inputClass}
            value={form.role}
            onChange={(e) => set("role", e.target.value)}
            disabled={isSelf}
          >
            <option value="user">Employee: only the sections ticked below</option>
            <option value="admin">Administrator: everything, including user management</option>
          </select>
        </Field>

        {form.role === "user" && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Sections this person can open</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {MODULES.map((m) => {
                const on = form.modules.includes(m.key);
                return (
                  <label
                    key={m.key}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      on ? "border-[#C00000]/50 bg-red-50/50" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input type="checkbox" checked={on} onChange={() => toggle(m.key)} className="mt-0.5 accent-[#C00000]" />
                    <span>
                      <span className="block text-sm font-medium text-[#111111]">{m.label}</span>
                      <span className="block text-xs text-gray-500">{m.desc}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={busy}>{existing ? "Save changes" : "Create user"}</Button>
        </div>
      </form>
    </Modal>
  );
}

function SecretModal({ secret, onClose }) {
  const [copied, setCopied] = useState(false);
  const text = `Username: ${secret.username}\nPassword: ${secret.password}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch {}
  };
  return (
    <Modal title="Share these credentials" onClose={onClose} width="max-w-md">
      <p className="text-sm text-gray-600">
        This password is shown <strong>only once</strong>. Copy it now and send it to the employee. You can set a new
        one at any time from the Password button.
      </p>
      <pre className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200 text-sm font-mono whitespace-pre-wrap break-all">{text}</pre>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="outline" onClick={copy}>
          {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
        </Button>
        <Button onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}
